import { useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Ruler, ClipboardList, AlertTriangle, ChevronRight } from 'lucide-react';
import { useOfferStore } from '@/lib/store';
import {
  addAufmassZeile,
  berechneAufmass,
  type Warnstufe,
} from '@shared/aufmass';
import type { PositionsAufmass } from '@shared/schema';
import { AufmassDetailModal } from './aufmass-detail-modal';
import { AufmassSyncButton } from './aufmass-sync-button';
import AufmassExport from '../aufmass-export';
import AufmassRechnungButton from '../aufmass-rechnung-button';

function parseMenge(value: string): number | undefined {
  if (value.trim() === '') return undefined;
  const n = Number(value.replace(',', '.'));
  return Number.isFinite(n) && n !== 0 ? n : undefined;
}

function formatMenge(value: number): string {
  return value.toLocaleString('de-DE', { maximumFractionDigits: 3 });
}

function formatEuro(value: number): string {
  return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function heute(): string {
  return new Date().toISOString().slice(0, 10);
}

// Ampel-Klassen je Warnstufe
const AMPEL: Record<Warnstufe, { dot: string; text: string; row: string }> = {
  ok: { dot: 'bg-emerald-500', text: 'text-emerald-700', row: '' },
  warnung: { dot: 'bg-amber-500', text: 'text-amber-700', row: 'bg-amber-50' },
  voll: { dot: 'bg-red-500', text: 'text-red-700', row: 'bg-red-50' },
  ueber: { dot: 'bg-red-600', text: 'text-red-700', row: 'bg-red-50' },
};

/**
 * Aufmaß-Maske: Mengenfortschreibung je LV-Position.
 * Schnell-Erfassung in der Ist-Spalte (Enter = neuer Eintrag mit heutigem Datum,
 * springt zur nächsten Position). Details je Position über Modal.
 */
export function AufmassView() {
  const positions = useOfferStore((s) => s.positions);
  const quoteItems = useOfferStore((s) => s.quoteItems);
  const updatePositionAufmass = useOfferStore((s) => s.updatePositionAufmass);
  const setAufmassView = useOfferStore((s) => s.setAufmassView);

  const containerRef = useRef<HTMLDivElement>(null);
  const [detailPositionId, setDetailPositionId] = useState<number | null>(null);

  const sollById = useMemo(() => {
    const map = new Map<number, number>();
    for (const item of quoteItems) {
      map.set(item.id, Number(item.menge) || 0);
    }
    return map;
  }, [quoteItems]);

  const rows = positions;

  const addMenge = (positionId: number, menge: number) => {
    const state = useOfferStore.getState();
    const position = state.positions.find((p) => p.id === positionId);
    if (!position) return;
    const neu = addAufmassZeile(position.aufmass, { datum: heute(), menge });
    state.updatePositionAufmass(positionId, neu);
  };

  const focusNext = (positionId: number) => {
    const container = containerRef.current;
    if (!container) return;
    const inputs = Array.from(
      container.querySelectorAll<HTMLInputElement>('input[data-aufmass-input="ist"]'),
    );
    const index = inputs.findIndex((el) => el.dataset.aufmassPosition === String(positionId));
    const next = index >= 0 ? inputs[index + 1] : undefined;
    if (next) {
      next.focus();
      next.select();
    }
  };

  const gesamt = useMemo(() => {
    let anzahl = 0;
    let istWert = 0;
    for (const position of rows) {
      const soll = sollById.get(position.id) ?? 0;
      const summen = berechneAufmass(position.aufmass, soll);
      if (summen.istMenge > 0) {
        anzahl += 1;
        istWert += summen.istMenge * (Number(position.preis) || 0);
      }
    }
    return { anzahl, istWert };
  }, [rows, sollById]);

  const detailPosition = detailPositionId !== null ? rows.find((p) => p.id === detailPositionId) : undefined;

  const handleDetailSave = (aufmass: PositionsAufmass | null) => {
    if (detailPositionId !== null) {
      updatePositionAufmass(detailPositionId, aufmass);
    }
  };

  return (
    <div ref={containerRef} className="flex h-full flex-col" data-testid="aufmass-view">
      {/* Kopfleiste */}
      <div className="flex flex-wrap items-center gap-4 border-b bg-teal-50 px-4 py-3">
        <div className="flex items-center gap-2 text-teal-700">
          <Ruler className="h-5 w-5" />
          <span className="font-semibold">Aufmaß</span>
        </div>
        <div className="ml-auto flex items-center gap-6 text-sm">
          <div className="flex items-center gap-1.5 text-gray-600">
            <ClipboardList className="h-4 w-4" />
            <span>
              <span className="font-semibold text-gray-800">{gesamt.anzahl}</span> Position{gesamt.anzahl !== 1 ? 'en' : ''} mit Aufmaß
            </span>
          </div>
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-wide text-gray-500">Ist-Wert gesamt</p>
            <p className="font-semibold text-teal-800">{formatEuro(gesamt.istWert)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-44"><AufmassExport /></div>
          <div className="w-52"><AufmassRechnungButton /></div>
          <div className="w-60"><AufmassSyncButton /></div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setAufmassView(false)}
            className="h-8 text-gray-600"
          >
            Zur Angebotstabelle
          </Button>
        </div>
      </div>

      {/* Tabelle */}
      <div className="min-h-0 flex-1 overflow-auto">
        {rows.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-500">
            Keine Positionen geladen. Bitte zuerst ein LV importieren oder Positionen anlegen.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-100 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 w-[90px]">Pos.</th>
                <th className="px-3 py-2">Bezeichnung</th>
                <th className="px-3 py-2 w-[130px] text-right">Soll-Menge</th>
                <th className="px-3 py-2 w-[110px] text-right">Ist-Menge</th>
                <th className="px-3 py-2 w-[110px] text-right">Rest-Menge</th>
                <th className="px-3 py-2 w-[150px] text-right">Auslastung</th>
                <th className="px-3 py-2 w-[170px]">+ Menge erfassen</th>
                <th className="px-3 py-2 w-[80px] text-center">Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((position, index) => {
                const soll = sollById.get(position.id) ?? 0;
                const summen = berechneAufmass(position.aufmass, soll);
                const ampel = AMPEL[summen.warnstufe];
                const gewarnt = summen.warnstufe === 'warnung' || summen.warnstufe === 'voll' || summen.warnstufe === 'ueber';
                const bezeichnung = position.name || position.beschreibung || '-';
                const rowBg = ampel.row || (index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50');
                return (
                  <tr key={position.id} className={`border-b border-gray-100 ${rowBg}`}>
                    <td className="px-3 py-1.5 font-medium text-blue-600">{position.positionNumber || '-'}</td>
                    <td className="max-w-[320px] truncate px-3 py-1.5" title={bezeichnung}>
                      <span className="inline-flex items-center gap-1.5">
                        {gewarnt && <AlertTriangle className={`h-4 w-4 flex-shrink-0 ${ampel.text}`} />}
                        <span className="truncate">{bezeichnung}</span>
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">
                      {soll > 0 ? `${formatMenge(soll)} ${position.einheit || ''}` : '-'}
                    </td>
                    <td className="px-3 py-1.5 text-right font-medium tabular-nums text-gray-800">
                      {summen.istMenge > 0 ? formatMenge(summen.istMenge) : '-'}
                    </td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${summen.restMenge < 0 ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                      {soll > 0 ? formatMenge(summen.restMenge) : '-'}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center justify-end gap-2">
                        <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${ampel.dot}`} title={summen.warnstufe} />
                        <span className={`tabular-nums ${ampel.text}`}>
                          {soll > 0 || summen.istMenge > 0 ? `${formatMenge(summen.auslastungProzent)} %` : '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        inputMode="decimal"
                        data-aufmass-input="ist"
                        data-aufmass-position={position.id}
                        placeholder="+ Menge"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const parsed = parseMenge(e.currentTarget.value);
                            if (parsed !== undefined) {
                              addMenge(position.id, parsed);
                              e.currentTarget.value = '';
                            }
                            focusNext(position.id);
                          }
                        }}
                        className="h-7 w-full rounded border border-gray-200 px-2 text-right text-sm focus:border-teal-400 focus:outline-none"
                      />
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setDetailPositionId(position.id)}
                        className="h-7 gap-1 px-2 text-xs text-gray-600 hover:text-teal-700 hover:bg-teal-50"
                        title="Aufmaß-Historie öffnen"
                      >
                        {summen.anzahlEintraege > 0 && (
                          <span className="tabular-nums">{summen.anzahlEintraege}</span>
                        )}
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {detailPosition && (
        <AufmassDetailModal
          open={detailPositionId !== null}
          onOpenChange={(open) => {
            if (!open) setDetailPositionId(null);
          }}
          positionNumber={detailPosition.positionNumber}
          bezeichnung={detailPosition.name || detailPosition.beschreibung || '-'}
          einheit={detailPosition.einheit || ''}
          sollMenge={sollById.get(detailPosition.id) ?? 0}
          aufmass={detailPosition.aufmass}
          onSave={handleDetailSave}
        />
      )}
    </div>
  );
}
